import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams, Link } from 'react-router-dom';
import { CheckCircle, Loader2, XCircle, Download, Package, Truck, Shield, ShoppingBag, Tag } from 'lucide-react';
import { ordersAPI } from '../lib/api';
import { Button } from '../components/ui/button';

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const walletPaid = searchParams.get('wallet') === 'true';
  const [status, setStatus] = useState('checking');
  const [order, setOrder] = useState(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    if (!orderId && !sessionId) { setStatus('error'); return; }

    // If fully paid with wallet, just fetch the order directly
    if (walletPaid && orderId) {
      ordersAPI.paymentStatus(orderId).then(res => {
        setOrder(res.data.order);
        setStatus('success');
      }).catch(() => setStatus('error'));
      return;
    }

    const check = async () => {
      try {
        const res = orderId
          ? await ordersAPI.paymentStatus(orderId)
          : await ordersAPI.checkoutStatus(sessionId);
        setOrder(res.data.order);
        if (res.data.status === 'paid') setStatus('success');
        else setStatus('pending');
      } catch {
        setStatus('error');
      }
    };
    check();
    const interval = setInterval(check, 3000);
    const timeout = setTimeout(() => { clearInterval(interval); if (status !== 'success') setStatus('timeout'); }, 60000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [orderId, sessionId]);

  const handlePrint = () => {
    window.print();
  };

  if (status === 'checking' || status === 'pending') {
    return (
      <div className="min-h-screen bg-[#F0F7FA] flex items-center justify-center px-4" data-testid="order-checking">
        <div className="bg-white rounded-2xl border border-[#D6E8F0] p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 text-[#0C63BF] animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#033871]">Vérification du paiement...</h2>
          <p className="text-[#5A8BA8] mt-2">Veuillez patienter quelques instants.</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'timeout') {
    return (
      <div className="min-h-screen bg-[#F0F7FA] flex items-center justify-center px-4" data-testid="order-error">
        <div className="bg-white rounded-2xl border border-[#D6E8F0] p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#033871]">Erreur de paiement</h2>
          <p className="text-[#5A8BA8] mt-2">Le paiement n'a pas pu être confirmé.</p>
          <Link to="/catalogue"><Button className="mt-6 bg-[#0C63BF]">Retour au catalogue</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F7FA] py-8 px-4" data-testid="order-success">
      <div className="max-w-2xl mx-auto">
        {/* Success banner */}
        <div className="bg-[#32CD32] rounded-2xl p-6 text-center mb-6" data-testid="payment-confirmed-banner">
          <CheckCircle className="w-14 h-14 text-white mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-white">Paiement confirmé !</h1>
          <p className="text-white/80 text-sm mt-1">Un email de confirmation vous a été envoyé.</p>
        </div>

        {/* Receipt */}
        <div ref={receiptRef} className="bg-white rounded-2xl border border-[#D6E8F0] overflow-hidden print:shadow-none" data-testid="payment-receipt">
          <div className="bg-[#021E47] px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-white font-bold text-lg">Preuve de paiement</h2>
              <p className="text-[#5A8BA8] text-xs mt-0.5">PadSmash - Marketplace Padel</p>
            </div>
            <div className="text-right">
              <p className="text-white/60 text-xs">Commande N°</p>
              <p className="text-white font-bold text-sm" data-testid="order-number">{order?.id?.slice(0, 8).toUpperCase()}</p>
            </div>
          </div>

          <div className="p-6 space-y-5">
            {/* Product info */}
            <div className="flex items-center gap-4 bg-[#F0F7FA] rounded-xl p-4" data-testid="receipt-product">
              <div className="w-12 h-12 rounded-lg bg-[#E8F4FD] flex items-center justify-center shrink-0">
                <Package className="w-6 h-6 text-[#0C63BF]" />
              </div>
              <div>
                <p className="text-xs text-[#5A8BA8] uppercase tracking-wider font-semibold">{order?.brand}</p>
                <p className="font-bold text-[#033871]">{order?.model}</p>
              </div>
            </div>

            {/* Shipping method */}
            {order?.shipping_method_name && (
              <div className="flex items-center gap-3 text-sm" data-testid="receipt-shipping-method">
                <Truck className="w-4 h-4 text-[#0C63BF] shrink-0" />
                <span className="text-[#5A8BA8]">Livraison :</span>
                <span className="text-[#033871] font-medium">{order.shipping_method_name}</span>
              </div>
            )}

            {/* Shipping address */}
            {order?.shipping && (
              <div className="flex items-start gap-3 text-sm" data-testid="receipt-shipping-address">
                <ShoppingBag className="w-4 h-4 text-[#0C63BF] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[#5A8BA8]">Adresse de livraison :</p>
                  <p className="text-[#033871] font-medium">{order.shipping.name}</p>
                  <p className="text-[#033871] text-xs">{order.shipping.address}, {order.shipping.postal_code} {order.shipping.city}</p>
                </div>
              </div>
            )}

            {/* Price breakdown */}
            <div className="border-t border-[#E8F4FD] pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#5A8BA8]">Prix article</span>
                <span className="text-[#033871] font-semibold" data-testid="receipt-item-price">{order?.item_price?.toFixed(2)} &euro;</span>
              </div>
              {order?.promo_discount > 0 && (
                <div className="flex justify-between text-sm" data-testid="receipt-promo-discount">
                  <span className="text-emerald-600 flex items-center gap-1"><Tag className="w-3 h-3" /> Code {order.promo_code}</span>
                  <span className="text-emerald-600 font-semibold">-{order.promo_discount.toFixed(2)} &euro;</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-[#5A8BA8]">Frais de livraison</span>
                <span className="text-[#033871] font-semibold" data-testid="receipt-shipping-cost">{order?.shipping_cost?.toFixed(2)} &euro;</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#5A8BA8] flex items-center gap-1"><Shield className="w-3 h-3 text-[#32CD32]" /> Protection acheteur</span>
                <span className="text-[#033871] font-semibold" data-testid="receipt-protection-fee">{order?.protection_fee?.toFixed(2)} &euro;</span>
              </div>
              <div className="border-t-2 border-[#E8F4FD] pt-3 flex justify-between">
                <span className="font-bold text-[#033871] text-lg">Total payé</span>
                <span className="font-black text-2xl text-[#0C63BF]" data-testid="receipt-total">{order?.total?.toFixed(2)} &euro;</span>
              </div>
            </div>

            {/* Date */}
            <div className="text-center text-xs text-[#B8D4E3] border-t border-[#E8F4FD] pt-4">
              <p>Paiement effectué le {new Date(order?.paid_at || order?.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
              <p className="mt-1">Vendeur : {order?.seller_name}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <Button onClick={handlePrint} variant="outline" className="flex-1 h-11 rounded-xl font-bold gap-2" data-testid="download-receipt-btn">
            <Download className="w-4 h-4" /> Télécharger la preuve
          </Button>
          <Link to="/dashboard" className="flex-1">
            <Button className="w-full bg-[#0C63BF] hover:bg-[#033871] h-11 rounded-xl font-bold gap-2" data-testid="go-to-orders-btn">
              <ShoppingBag className="w-4 h-4" /> Mes achats
            </Button>
          </Link>
          <Link to="/catalogue" className="flex-1">
            <Button variant="outline" className="w-full h-11 rounded-xl font-bold" data-testid="continue-shopping-btn">
              Continuer
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
